import React, { useState, useEffect, useRef } from 'react';
import { DiscordSDK } from '@discord/embedded-app-sdk';
import type {
  Participant,
  SyncMessage,
  ChatMessage,
  VideoPlayerMessage,
  DiscordActivity,
  AuthorizeResponse,
  TokenResponse,
  AuthenticateResponse,
  InstanceConnectedParticipants,
} from './types';

const App: React.FC = () => {
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [participants, setParticipants] = useState<Map<string, Participant>>(
    new Map()
  );
  const [videoInput, setVideoInput] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentTime, setCurrentTime] = useState('0:00');

  const discordRef = useRef<DiscordSDK | null>(null);

  useEffect(() => {
    initializeDiscord();
    setupEventListeners();
  }, []);

  const initializeDiscord = async () => {
    try {
      const discord = new DiscordSDK(
        import.meta.env.VITE_DISCORD_CLIENT_ID as string
      );
      discordRef.current = discord;

      await discord.ready();

      const { code }: AuthorizeResponse = await discord.commands.authorize({
        client_id: import.meta.env.VITE_DISCORD_CLIENT_ID as string,
        response_type: 'code',
        state: '',
        prompt: 'none',
        scope: ['identify', 'applications.commands'],
      });

      const response = await fetch('/api/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const { access_token }: TokenResponse = await response.json();

      const auth: AuthenticateResponse | null =
        await discord.commands.authenticate({
          access_token,
        });

      if (auth == null) {
        throw new Error('Authenticate command failed');
      }

      await joinRoom();
    } catch (error) {
      console.error('Failed to initialize Discord SDK:', error);
    }
  };

  const setupEventListeners = () => {
    window.addEventListener(
      'message',
      (event: MessageEvent<VideoPlayerMessage>) => {
        if (event.origin !== 'https://embed.nicovideo.jp') return;

        const data: VideoPlayerMessage = event.data;
        if (
          data.eventName === 'playerMetadataChange' &&
          data.data.currentTime !== undefined
        ) {
          updateCurrentTime(data.data.currentTime);
        }
      }
    );
  };

  const joinRoom = async () => {
    if (!discordRef.current) return;

    try {
      const activityInfo: InstanceConnectedParticipants =
        await discordRef.current.commands.getInstanceConnectedParticipants();

      const newParticipants = new Map<string, Participant>();
      activityInfo.participants.forEach(participant => {
        newParticipants.set(participant.id, {
          id: participant.id,
          username: participant.username,
          avatar: participant.avatar,
        });
      });

      setParticipants(newParticipants);
    } catch (error) {
      console.error('Failed to get participants:', error);
    }
  };

  const extractVideoId = (input: string): string | null => {
    if (!input) return null;

    const urlPattern =
      /(?:https?:\/\/)?(?:www\.)?nicovideo\.jp\/watch\/([a-z]{2}\d+)/;
    const idPattern = /^([a-z]{2}\d+)$/;

    const urlMatch = input.match(urlPattern);
    if (urlMatch) return urlMatch[1];

    const idMatch = input.match(idPattern);
    if (idMatch) return idMatch[1];

    return null;
  };

  const loadVideo = (videoId: string) => {
    console.log('Loading video:', videoId);
    setCurrentVideo(videoId);
    broadcastAction('videoChange', { videoId });
  };

  const handleLoadVideo = () => {
    const input = videoInput.trim();
    console.log('Input value:', input);

    if (!input) {
      alert('Please enter a video URL or ID');
      return;
    }

    const videoId = extractVideoId(input);
    console.log('Extracted video ID:', videoId);

    if (videoId) {
      loadVideo(videoId);
    } else {
      alert(
        'Invalid video URL or ID. Please use format: sm1234567 or https://www.nicovideo.jp/watch/sm1234567'
      );
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLoadVideo();
    }
  };

  const broadcastAction = (
    action: string,
    data: Record<string, unknown> = {}
  ) => {
    const message: SyncMessage = {
      type: 'sync',
      action,
      data,
      timestamp: Date.now(),
      userId: getCurrentUserId(),
    };

    try {
      if (discordRef.current) {
        const activity: DiscordActivity = {
          details: 'Watching Niconico Video Together',
          state: currentVideo
            ? `Video: ${currentVideo}`
            : 'Waiting for video...',
          timestamps: {
            start: Date.now(),
          },
        };
        discordRef.current.commands.setActivity({ activity });
      }
    } catch (error) {
      console.error('Failed to set activity:', error);
    }

    console.log('Broadcasting action:', message);
  };

  const getCurrentUserId = (): string => {
    return 'user-' + Math.random().toString(36).substring(2, 11);
  };

  const sendChatMessage = (message: string) => {
    if (!message.trim()) return;

    const chatMessage: ChatMessage = {
      type: 'chat',
      message: message.trim(),
      userId: getCurrentUserId(),
      username: 'You',
      timestamp: Date.now(),
    };

    setChatMessages((prev: ChatMessage[]) => [...prev, chatMessage]);
    broadcastAction('chat', chatMessage as Record<string, unknown>);
  };

  const handleSendChat = () => {
    sendChatMessage(chatInput);
    setChatInput('');
  };

  const handleChatKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSendChat();
    }
  };

  const updateCurrentTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    setCurrentTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
  };

  return (
    <div id="app">
      <header className="header">
        <h1>Niconico Video Together</h1>
        <div className="controls">
          <input
            type="text"
            value={videoInput}
            onChange={e => setVideoInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Video URL or ID (sm1234567)"
          />
          <button onClick={handleLoadVideo}>Load Video</button>
        </div>
      </header>

      <main className="main">
        <div className="video-container">
          <VideoPlayer videoId={currentVideo} />
        </div>

        <div className="sidebar">
          <div className="participants">
            <h3>Participants</h3>
            <div className="participants-list">
              {Array.from(participants.values()).map(participant => (
                <ParticipantItem
                  key={participant.id}
                  participant={participant}
                />
              ))}
            </div>
          </div>

          <div className="sync-controls">
            <h3>Sync Controls</h3>
            <button onClick={() => broadcastAction('play')}>
              Play Together
            </button>
            <button onClick={() => broadcastAction('pause')}>
              Pause Together
            </button>
            <div className="sync-time">
              <label>
                Current Time: <span>{currentTime}</span>
              </label>
            </div>
          </div>

          <div className="chat">
            <h3>Chat</h3>
            <div className="chat-messages">
              {chatMessages.map((message, index) => (
                <ChatMessageItem key={index} message={message} />
              ))}
            </div>
            <div className="chat-input">
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyPress={handleChatKeyPress}
                placeholder="Type a message..."
              />
              <button onClick={handleSendChat}>Send</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

interface VideoPlayerProps {
  videoId: string | null;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoId }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (videoId && containerRef.current) {
      tryEmbedMethods(containerRef.current, videoId);
    }
  }, [videoId]);

  const tryEmbedMethods = (container: HTMLElement, videoId: string) => {
    // Clear previous content
    container.innerHTML = '';

    // Try script-based embedding first (Niconico's recommended method)
    tryScriptMethod(container, videoId);
  };

  const tryScriptMethod = (container: HTMLElement, videoId: string) => {
    // Clear container
    container.innerHTML = '';

    // Method 1: Script-based embedding (Niconico's preferred method for 2025)
    const scriptElement = document.createElement('script');
    scriptElement.type = 'application/javascript';
    scriptElement.src = `https://embed.nicovideo.jp/watch/${videoId}/script?w=854&h=480`;
    scriptElement.async = true;

    const noscriptElement = document.createElement('noscript');
    noscriptElement.innerHTML = `<a href="https://www.nicovideo.jp/watch/${videoId}">View on Niconico</a>`;

    let scriptTimeout: NodeJS.Timeout;

    scriptElement.onload = () => {
      console.log('Script-based embedding loaded successfully');
      clearTimeout(scriptTimeout);
    };

    scriptElement.onerror = () => {
      console.error('Script-based embedding failed, trying iframe method');
      clearTimeout(scriptTimeout);
      tryIframeMethod(container, videoId);
    };

    // Set timeout for script loading
    scriptTimeout = setTimeout(() => {
      console.log('Script load timeout, trying iframe method');
      tryIframeMethod(container, videoId);
    }, 10000);

    container.appendChild(scriptElement);
    container.appendChild(noscriptElement);
  };

  const tryIframeMethod = (container: HTMLElement, videoId: string) => {
    // Clear container
    container.innerHTML = '';

    // Method 2: Direct iframe embedding (fallback)
    const iframe = document.createElement('iframe');
    iframe.id = 'nicoPlayer';
    iframe.src = `https://embed.nicovideo.jp/watch/${videoId}`;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');

    let iframeTimeout: NodeJS.Timeout;

    iframe.onload = () => {
      console.log('Iframe embedding loaded successfully');
      clearTimeout(iframeTimeout);
    };

    iframe.onerror = () => {
      console.error('Iframe embedding failed');
      clearTimeout(iframeTimeout);
      showEmbedError(container, videoId);
    };

    // Set timeout for iframe loading
    iframeTimeout = setTimeout(() => {
      console.log('Iframe load timeout');
      showEmbedError(container, videoId);
    }, 10000);

    container.appendChild(iframe);
  };

  const showEmbedError = (container: HTMLElement, videoId: string) => {
    container.innerHTML = `
      <div class="placeholder">
        <p>Failed to load video: ${videoId}</p>
        <p>This video may not be available for embedding or may not exist.</p>
        <p><a href="https://www.nicovideo.jp/watch/${videoId}" target="_blank" rel="noopener">View on Niconico</a></p>
        <p style="font-size: 0.8em; color: #888;">If you continue to experience issues, the video may be restricted or removed.</p>
      </div>
    `;
  };

  if (!videoId) {
    return (
      <div className="placeholder">
        <p>Enter a video URL or ID to get started</p>
        <p>Example: sm9 or https://www.nicovideo.jp/watch/sm9</p>
      </div>
    );
  }

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

interface ParticipantItemProps {
  participant: Participant;
}

const ParticipantItem: React.FC<ParticipantItemProps> = ({ participant }) => {
  const avatarUrl = participant.avatar || '';

  return (
    <div className="participant">
      <div
        className="participant-avatar"
        style={{ backgroundImage: `url('${avatarUrl}')` }}
      />
      <span>{participant.username}</span>
    </div>
  );
};

interface ChatMessageItemProps {
  message: ChatMessage;
}

const ChatMessageItem: React.FC<ChatMessageItemProps> = ({ message }) => {
  const time = new Date(message.timestamp).toLocaleTimeString();

  return (
    <div className="chat-message">
      <span className="author">{message.username}</span>
      <span className="time">{time}</span>
      <div className="message">{message.message}</div>
    </div>
  );
};

export default App;
